import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelWorkOrderSnLabelJob,
  createWorkOrderSnLabelJob,
  fetchWorkOrderSnLabelJobs,
} from '#/api/momMgmt/workOrder/snLabelJob';
import useDetailTable from '#/hooks/table/useDetailTable';
import { StatusBadge } from '#/modules/commonComponents/badge';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  SN_LABEL_TYPE_OPTIONS,
  SN_PRINTER_PROFILE_OPTIONS,
  SN_PRINT_RANGE_OPTIONS,
  canPrintSnLabels,
  formatSnLabelJobStatus,
  formatSnLabelType,
  formatSnPrinterProfile,
  hasActiveSnLabelJob,
  isSnLabelJobCancellable,
  resolveSnLabelJobErrorMessage,
  validateSnPrintRange,
} from '#/modules/momMgmt/workOrder/helper/snLabelJob';
import { formatDateTimeOrDash } from '#/utils/date';
import { toast } from '#/utils/toast';

const POLL_INTERVAL_MS = 3000;

function snLabelJobsQueryKey(workOrderId) {
  return ['erp', 'workOrder', 'snLabelJobs', workOrderId];
}

function getSnLabelErrorMessage(error, fallback) {
  const code = error?.response?.data?.code;
  return resolveSnLabelJobErrorMessage(code) ?? getWorkOrderApiErrorMessage(error, fallback);
}

function formatRange(start, end) {
  if (start == null && end == null) {
    return '—';
  }
  return start === end ? String(start) : `${start ?? '?'}–${end ?? '?'}`;
}

function buildJobColumns({ onCancel, cancellingId }) {
  return [
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      // DetailTable 對 id=status 預設畫全站狀態 icon；這裡用自訂中文標籤
      meta: { statusPresentation: 'badge' },
      size: 110,
      enableSorting: false,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          label={formatSnLabelJobStatus(row.original.status)}
          size="sm"
        />
      ),
    },
    {
      id: 'printerProfile',
      header: '印表機',
      accessorFn: (row) => formatSnPrinterProfile(row.printerProfile),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'labelType',
      header: '標籤',
      accessorFn: (row) => formatSnLabelType(row.labelType),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'range',
      header: '流水號',
      accessorFn: (row) => formatRange(row.serialStart, row.serialEnd),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'progress',
      header: '已確認送出至',
      accessorFn: (row) => (row.lastSentSerialEnd == null ? '—' : String(row.lastSentSerialEnd)),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'uncertain',
      header: '可能已送出',
      accessorFn: (row) => (
        row.uncertainSerialStart == null
          ? '—'
          : formatRange(row.uncertainSerialStart, row.uncertainSerialEnd)
      ),
      size: 110,
      enableSorting: false,
    },
    {
      id: 'createdAt',
      header: '建立時間',
      accessorFn: (row) => formatDateTimeOrDash(row.createdAt),
      size: 150,
      enableSorting: false,
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      enableSorting: false,
      cell: ({ row }) => (isSnLabelJobCancellable(row.original) ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={cancellingId === row.original.id}
          onClick={() => onCancel(row.original)}
        >
          取消
        </Button>
      ) : null),
    },
  ];
}

/**
 * 工單預計 SN 標籤送印：選印表機、標籤類型、整段或連續子範圍 → 建立背景 job；
 * 下方列出 24 小時內的 job，排隊／送印中自動輪詢。
 */
const WorkOrderSnLabelPanel = ({ workOrderId, serialAllocation }) => {
  const queryClient = useQueryClient();
  const [rangeError, setRangeError] = useState(null);
  const canPrint = canPrintSnLabels(serialAllocation);

  const jobsQuery = useQuery({
    queryKey: snLabelJobsQueryKey(workOrderId),
    queryFn: () => fetchWorkOrderSnLabelJobs(workOrderId),
    enabled: Boolean(workOrderId) && Boolean(serialAllocation),
    refetchInterval: (query) => (
      hasActiveSnLabelJob(query.state.data?.data) ? POLL_INTERVAL_MS : false
    ),
  });
  const jobs = useMemo(() => jobsQuery.data?.data ?? [], [jobsQuery.data?.data]);

  const { control, register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      printerProfile: '',
      labelType: 'T_LABEL',
      rangeMode: 'ALL',
      serialStart: serialAllocation?.serialStart ?? '',
      serialEnd: serialAllocation?.serialEnd ?? '',
    },
  });
  const rangeMode = useWatch({ control, name: 'rangeMode' });

  const createMutation = useMutation({
    mutationFn: (values) => createWorkOrderSnLabelJob(workOrderId, {
      ...values,
      wholeRange: values.rangeMode === 'ALL',
    }),
    onSuccess: () => {
      toast.success('已建立送印工作');
      queryClient.invalidateQueries({ queryKey: snLabelJobsQueryKey(workOrderId) });
    },
    // 失敗保留表單內容
    onError: (error) => toast.error(getSnLabelErrorMessage(error, '建立送印工作失敗')),
  });

  const cancelMutation = useMutation({
    mutationFn: (job) => cancelWorkOrderSnLabelJob(workOrderId, job.id),
    onSuccess: () => toast.success('已取消送印工作'),
    onError: (error) => toast.error(getSnLabelErrorMessage(error, '取消失敗')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: snLabelJobsQueryKey(workOrderId) }),
  });

  const { mutate: cancelJob, isPending: isCancelling, variables: cancellingJob } = cancelMutation;
  const cancellingId = isCancelling ? cancellingJob?.id : null;
  const columns = useMemo(
    () => buildJobColumns({ onCancel: cancelJob, cancellingId }),
    [cancelJob, cancellingId],
  );
  const detailTableState = useDetailTable(jobs, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  function onSubmit(values) {
    if (!values.printerProfile) {
      setRangeError('請選擇印表機');
      return;
    }
    if (values.rangeMode === 'PARTIAL') {
      const message = validateSnPrintRange(values, serialAllocation);
      if (message) {
        setRangeError(message);
        return;
      }
    }
    setRangeError(null);
    createMutation.mutate(values);
  }

  if (!serialAllocation) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">工單核准後配置 SN 號段，才能列印預計 SN 標籤。</p>;
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      <p className="text-sm text-slate-700">
        號段：{serialAllocation.productCode ?? '—'}
        {' · '}
        {serialAllocation.serialYear ?? '—'}（{serialAllocation.serialYearCode ?? '—'}）
        {' · '}
        流水號 {formatRange(serialAllocation.serialStart, serialAllocation.serialEnd)}
      </p>

      {canPrint ? (
        <form
          className="grid grid-cols-1 gap-3 md:grid-cols-3"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <FloatingSelect
            id="printerProfile"
            label="印表機"
            control={control}
            errors={errors}
            options={SN_PRINTER_PROFILE_OPTIONS}
            allowEmpty
          />
          <FloatingSelect
            id="labelType"
            label="標籤類型"
            control={control}
            errors={errors}
            options={SN_LABEL_TYPE_OPTIONS}
          />
          <FloatingSelect
            id="rangeMode"
            label="列印範圍"
            control={control}
            errors={errors}
            options={SN_PRINT_RANGE_OPTIONS}
          />
          {rangeMode === 'PARTIAL' ? (
            <>
              <FloatingInput
                id="serialStart"
                label="起始流水號"
                type="number"
                register={register}
                errors={errors}
              />
              <FloatingInput
                id="serialEnd"
                label="結束流水號"
                type="number"
                register={register}
                errors={errors}
              />
            </>
          ) : null}
          <div className="col-span-full flex flex-wrap items-center justify-end gap-3">
            {rangeError ? <p className="text-sm text-destructive">{rangeError}</p> : null}
            <Button type="submit" size="sm" disabled={createMutation.isPending}>
              {createMutation.isPending ? '送出中…' : '送印'}
            </Button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">此號段已釋放，無法列印。</p>
      )}

      <p className="text-xs text-muted-foreground">
        「已送出」只代表列印指令已送到印表機，不代表標籤已實際印出；結果不明時請依「可能已送出」範圍人工確認後，另建不重疊的送印工作。紀錄保留 24 小時。
      </p>

      {jobsQuery.isError ? (
        <p className="text-sm text-destructive">
          {getSnLabelErrorMessage(jobsQuery.error, '載入送印工作失敗')}
        </p>
      ) : null}
      {jobs.length === 0 && !jobsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">24 小時內沒有送印工作</p>
      ) : null}
      {jobs.length > 0 ? (
        <div className="detail-table-container">
          <DetailTable
            state={detailTableState}
            containerClassName="detail-table-scroll--section-embedded"
            tableClassName="detail-table--flat-lines"
          />
        </div>
      ) : null}
    </div>
  );
};

WorkOrderSnLabelPanel.propTypes = {
  workOrderId: PropTypes.string,
  serialAllocation: PropTypes.shape({
    productCode: PropTypes.string,
    serialYear: PropTypes.number,
    serialYearCode: PropTypes.string,
    serialStart: PropTypes.number,
    serialEnd: PropTypes.number,
    status: PropTypes.string,
  }),
};

export default WorkOrderSnLabelPanel;
